import { WebappApplicationFragment, useUpdateApplicationMutation } from '@gql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { FileUploadInput } from '@/common/atoms/FileUploadInput'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

type AuthenticActFormProps = {
  application: WebappApplicationFragment
}

// Authentic Act form schema and type
const authenticActSchema = yup.object().shape({
  signatureDate: yup.date().required('La date de signature est requise'),
  fileId: yup.string().required('Le document est requis'),
})

type AuthenticActFormData = yup.InferType<typeof authenticActSchema>

export const AuthenticActForm = ({ application }: AuthenticActFormProps) => {
  const { t } = useTranslation()

  const [updateApplication] = useUpdateApplicationMutation({
    refetchQueries: ['applications'],
  })

  // Authentic Act form
  const authenticActForm = useForm<AuthenticActFormData>({
    resolver: yupResolver(authenticActSchema),
    defaultValues: {
      signatureDate: application.authenticActSignatureDate
        ? new Date(application.authenticActSignatureDate)
        : undefined,
      fileId: application.authenticActFileId || '',
    },
  })

  const onAuthenticActSubmit = authenticActForm.handleSubmit(
    async (data: AuthenticActFormData) => {
      if (!application || application.archived) return

      try {
        await updateApplication({
          variables: {
            id: application.id,
            values: {
              authenticActSignatureDate: data.signatureDate.toISOString(),
              authenticActFileId: data.fileId,
            },
          },
        })
        toast({
          title: t('ApplicationSheet.authenticAct.success.title'),
          description: t('ApplicationSheet.authenticAct.success.description'),
          variant: 'success',
        })
      } catch (error) {
        console.error(error)
        toast({
          title: t('ApplicationSheet.authenticAct.error.title'),
          description: t('ApplicationSheet.authenticAct.error.description'),
          variant: 'destructive',
        })
      }
    }
  )

  return (
    <div className="tw-border tw-rounded-lg tw-p-4 tw-space-y-4">
      <h3 className="tw-text-lg tw-font-semibold">
        {t('ApplicationSheet.authenticAct.title')}
      </h3>
      <Form {...authenticActForm}>
        <form onSubmit={onAuthenticActSubmit} className="tw-space-y-4">
          <FormField
            control={authenticActForm.control}
            name="signatureDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('ApplicationSheet.authenticAct.signatureDate')}
                </FormLabel>
                <FormControl>
                  <InputDatePicker
                    value={field.value}
                    onChange={field.onChange}
                    className="tw-w-full"
                    placeholder={t(
                      'ApplicationSheet.authenticAct.datePlaceholder'
                    )}
                    fromYear={2020}
                    toYear={2030}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={authenticActForm.control}
            name="fileId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('ApplicationSheet.authenticAct.file')}</FormLabel>
                <FormControl>
                  <FileUploadInput
                    value={field.value}
                    onChange={field.onChange}
                    storageName="authentic-act"
                    accept=".pdf,.doc,.docx"
                    className="tw-w-full"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" disabled={!authenticActForm.formState.isValid}>
            {t('ApplicationSheet.authenticAct.save')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
